import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Paste a PR/MR URL and review it right now. Unlike the config wizard this needs
// no PR discovery pass, works on PRs a scheduled scan would filter out (drafts,
// excluded titles, merged/closed), and always re-reviews — so the same MR can be
// reviewed as many times as needed.
export default function ReviewPrNowModal({ onClose, defaultUrl = '', defaultConfigId = '' }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState(defaultUrl);
  const [configId, setConfigId] = useState(defaultConfigId ? String(defaultConfigId) : '');
  const [configs, setConfigs] = useState([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  // The config supplies the model, rules, packs and post_mode. Left on auto-detect
  // the server picks the config that covers the PR's repository.
  useEffect(() => {
    axios.get('/code-review/configs')
      .then(res => setConfigs(res.data.data || []))
      .catch(() => setConfigs([]));
  }, []);

  async function start() {
    if (!url.trim()) { setError('Paste a pull request / merge request URL first.'); return; }
    setStarting(true);
    setError('');
    try {
      const res = await axios.post('/code-review/review-pr', {
        pr_url: url.trim(),
        config_id: configId ? Number(configId) : undefined,
      });
      onClose();
      if (res.data.review_run_id) navigate(`/code-review/runs/${res.data.review_run_id}`);
      else navigate('/code-review/runs');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start the review');
      setStarting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: '92vw', padding: 24 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Review a pull request now</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          The review starts immediately — it doesn&apos;t wait for a schedule — and runs even if
          this PR was reviewed before, so you can re-review after pushing changes.
        </p>

        {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

        <label className="form-label">Pull request / merge request URL</label>
        <input className="form-control" autoFocus value={url} placeholder="https://bitbucket.org/workspace/repo/pull-requests/123"
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !starting) start(); }} />
        <p className="form-hint" style={{ marginBottom: 14 }}>
          GitHub, GitLab, Bitbucket and Gitea URLs are supported.
        </p>

        <label className="form-label">Review config</label>
        <select className="form-control" value={configId} onChange={e => setConfigId(e.target.value)}>
          <option value="">Auto-detect from the repository</option>
          {configs.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}{c.enabled ? '' : ' (disabled)'} · {c.post_mode} · {c.llm_model}
            </option>
          ))}
        </select>
        <p className="form-hint" style={{ marginBottom: 18 }}>
          Sets the model, rules and whether findings are posted to the PR or kept as a dry run.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={starting}>Cancel</button>
          <button className="btn btn-primary" onClick={start} disabled={starting || !url.trim()}>
            {starting ? 'Starting…' : 'Review now'}
          </button>
        </div>
      </div>
    </div>
  );
}
